import { BareFetcher, PublicConfiguration } from 'swr/_internal'
import { EntryDataType } from 'types/helpers'

/**
 * All Rolodex entries have a string "type" which implies their data schema.
 */
export enum RolodexEntryType {
  // Main hierarchy
  ORGANIZATION = 'organization',
  SITE = 'site',
  SITE_MODEL = 'site-model',
  UNIT = 'unit',
  COMPONENT = 'component',
  // Job-related
  JOB = 'job',
  INSPECTION = 'inspection',
  REPORT = 'report',
  NDE = 'nde',
  // Visual inspection types
  VISUAL_INSPECTION = 'visual-inspection',
  VISUAL_INSPECTION_FORM = 'visual-inspection-form',
  VISUAL_INSPECTION_SECTION = 'visual-inspection-section',
  VISUAL_INSPECTION_FIELD = 'visual-inspection-field',
  VISUAL_INSPECTION_FORM_RESPONSE = 'visual-inspection-form-response',
  VISUAL_INSPECTION_SECTION_RESPONSE = 'visual-inspection-section-response',
  VISUAL_INSPECTION_FIELD_RESPONSE = 'visual-inspection-field-response',
  PHOTOGRAMMETRY_MODEL = 'photogrammetry-model',
  GEOMETRIC_PIN = 'geometric-pin',
  // Component artifacts
  MARKUP_LAYER = 'markup-layer',
  COMPONENT_DEFAULTS = 'component-defaults',
  COMPONENT_DATA_FRAME = 'component-data-frame',
  COMPONENT_DATASET = 'component-dataset',
  ACTION_LOG = 'action-log',
  DUCK_DB_FEATURE_FLAG = 'forecast-duckdb-feature-flag',

  // INVENTORY
  EQUIPMENT = 'equipment',
  EQUIPMENT_CONFIGURATION = 'equipment-configuration',
  ASSEMBLY = 'assembly',
  ASSEMBLY_CONFIGURATION = 'assembly-configuration',
  WORK_ORDER = 'work-order',
  SUB_ASSEMBLY = 'sub-assembly',

  // Process Flow Diagram
  PROCESS_FLOW_DIAGRAM = 'process-flow-diagram',

  // FDE types
  FDE_FORECAST_COMPONENT_CONFIG = 'forecast-component-config',
  ASSET_OUTAGE = 'asset-outage',
  FDE_UNIT = 'fde-customer-unit',
  REPAIR_PLAN = 'repair-plan',
  REPAIR_PLAN_CONFIG = 'repair-plan-config',
  REPAIR_SCENARIO = 'repair-scenario',
  REPLACEMENT_PLAN = 'replacement-plan',
  COMPONENT_HEALTH_PLAN = 'component-health-plan',
  FDE_INSPECTION_METADATA = 'fde-inspection-metadata',
  FDE_COMPONENT_DATA_FRAME = 'fde-component-data-frame',
  DERATING_SCENARIO = 'fde-forecast-derating-scenario',
  COMPONENT_HEALTH_PLAN_ACTION = 'component-health-plan-action',
  INSPECTION_SCHEDULE_CONFIG_TYPE = 'fde-am-inspection-schedule-config',
  INSPECTION_OBJECT = 'fde-am-inspection',
  PIPE_WRAP = 'pipe-wrap',
  ASSET_MODEL = 'asset-model',
  THIRD_PARTY_INSPECTION_DETAILS = 'third-party-inspection-details',
  COMBINED_INSPECTION_METRICS = 'combined-inspection-metrics',
  UT_SCOPE_VALIDATION = 'ut-scope-validation',
  SITE_CONFIG = 'site-config',
  AS_PIPELINE_STATUS = 'as-pipeline-status',
  INDICATION = 'indication',
  INDICATION_SET = 'indication-set',
  LINE_AVERAGE_MARKUP_LAYER = 'line-average-ml',
  INSPECTION_REPORT_FORM = 'inspection-report-form',
  MICROSCOPE_REGION_OF_INTEREST = 'microscope-region-of-interest',
  UNIT_NII_METADATA = 'unit-nii-metadata',
  DRILLDOWN_EXPLORATION = 'drilldown-exploration',
  INDICATION_DAMAGE_AREA = 'indication-damage-area',
  KESTREL_MODEL = 'kestrel-model',
  RULER = 'ruler',
  ASSET_HEALTH_PLAN = 'asset-health-plan',
  FDE_AM_BATCH = 'fde-am-batch',
  COMPONENT_DATA_FRAME_CHUNKS = 'component-data-frame-chunks',
  FFS_CRITICAL_THICKNESS_PROFILE = 'ffs-critical-thickness-profile',
  EXPLORER_DATA_SCHEMA = 'explorer-data-schema',
  INDICATION_RECOMMENDATION = 'indication-recommendation',
  BUMBLEBEE_ASSET = 'bumblebee-asset',
  INDICATION_PHOTOGRAMMETRY = 'indication-photogrammetry',
  RIO_INDICATION_OVERVIEW = 'rio-indication-overview',
  ADVANCED_SERVICES_SCAN = 'advanced-services-scan',
  ROTATING = 'rotating',
  INDICATION_REPAIR_PLAN = 'indication-repair-plan',

  // Salesforce
  SALESFORCE_ACCOUNT = 'sf-account',
  SALESFORCE_JOB = 'sf-job',
  SALESFORCE_RUG_COMPONENT_ASSIGNMENT = 'sf-rug-component-job-assignment',
  SALESFORCE_RUG_ASSET = 'sf-rug-asset',
  SALESFORCE_RUG_COMPONENT = 'sf-rug-component',
  SALESFORCE_RUG_SUBCOMPONENT = 'sf-rug-subcomponent',
  SALESFORCE_RUG_SUBCOMPONENT_ASSIGNMENT = 'sf-rug-subcomponent-job-assignment',
  SITE_CONFIG_TYPE = 'fde-asset-manager-site-config',
  EXTENSION_CONFIG = 'forecast-extension-config',
  // Point clouds
  POTREE_POINT_CLOUD = 'potree-point-cloud',
  POINT_CLOUD_SCENE = 'point-cloud-scene',
  OBSERVATION = 'observation',
  OBSERVATION_TICKET = 'observation-ticket',
  MILL_PLAN = 'mill-plan',
  GEOGRAPHIC_PIN = 'geographic-pin',
  USER_COMMENT = 'user-comment',
  WALKDOWN = 'walkdown',
  WALKDOWN_REPORT_LINK = 'walkdown-report-link',
  WALKDOWN_UNIT_LINK = 'walkdown-unit-link',
  INSPECTION_ASSESSMENT = 'inspection-assessment',
  RISK_ASSESSMENT = 'risk-assessment',
  // Asset Lens types
  ASSET_LENS_SENSOR_TAG = 'asset-lens-sensor-tag',
  ASSET_LENS_SENSOR_DATA = 'asset-lens-sensor-data',
  ASSET_LENS_WORK_ORDER = 'asset-lens-work-order',
  ASSET_LENS_IR_IMAGE = 'asset-lens-ir-image',
  ASSET_LENS_ACOUSTIC_IMAGE = 'asset-lens-acoustic-image',
  ASSET_LENS_MAINTENANCE_RECORD = 'asset-lens-maintenance-record',
  ASSET_LENS_ENVIRONMENTAL_DATA = 'asset-lens-environmental-data',
  ASSET_LENS_DOCUMENT = 'asset-lens-document',
  ASSET_LENS_LOCATION = 'asset-lens-location',
  ASSET_LENS_EQUIPMENT = 'asset-lens-equipment',
  ASSET_LENS_INVENTORY_ITEM = 'asset-lens-inventory-item',
  ASSET_LENS_INVENTORY_STOCK = 'asset-lens-inventory-stock',
  SANDBOX = 'sandbox',
}

/**
 * Rolodex entries include a JSON blob of associated data, typically defined by
 * their type's schema.
 */
export type RolodexEntryDataType = Record<string, unknown>

/** Rolodex entries may be tagged to other Rolodex entries by their type names */
export type RolodexEntryTagsType = Partial<
  Record<RolodexEntryType, string | null>
>

/** Inspection entries in Rolodex have a special index for fast image lookup */
export type RolodexEntryIndexType = Record<string, string>

/** A type representing filters to use when querying */
export type RolodexFilterType = {
  type?: string
  field?: string
  key?: string | null
  op?: RolodexFilterOp | string
  value?: string | string[] | number | number[] | null
  cast?: string
}

export enum RolodexFilterOp {
  EQUAL = '=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN_OR_EQUAL = '<=',
  NOT_EQUAL = '!=',
  CONTAINS = 'contains',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_NULL = 'is_null',
  NOT_NULL = 'not_null',
  TRUTHY = 'truthy',
  FALSY = 'falsy',
  NULL = 'null',
  REGEX = '~',
  NOT_REGEX = '!~',
}

/**
 * A Rolodex entry consists of a unique string ID, a type, a JSON blob of
 * associated data, and tags to other Rolodex entries by their type names.
 */
export type RolodexEntryJson<
  TYPE_GENERIC extends RolodexEntryType = RolodexEntryType,
  DATA_GENERIC extends EntryDataType = EntryDataType,
> = {
  id: string
  type: TYPE_GENERIC
  data: DATA_GENERIC
  tags: RolodexEntryTagsType
  index: RolodexEntryIndexType
  generation: number
  updated_at: string
  created_at?: string
  view_restriction: 'internal' | null
}

/**
 * The JSON required to perform a Rolodex "put" operation (upserting an entry)
 */
export type RolodexEntryPutJson<
  Data extends RolodexEntryDataType = RolodexEntryDataType,
  Tags extends RolodexEntryTagsType = RolodexEntryTagsType,
> = {
  id: string
  type: RolodexEntryType
  data: Data
  tags: Tags
  index?: RolodexEntryIndexType
  generation?: number
  updated_at?: string
}

/**
 * The JSON required to perform a Rolodex "patch" operation (adding in new
 * data fields and/or tags)
 */
export type RolodexEntryPatchJson = {
  id: string
  type?: RolodexEntryType
  data?: RolodexEntryDataType
  delete_data?: string[]
  tags?: RolodexEntryTagsType
}

/**
 * The JSON required to perform a Rolodex "patch" operation to modify view
 * restrictions on an entry
 */
export type RolodexViewRestrictionPatchJson = {
  restriction: 'internal' | null
  entry_ids: string[]
}

/**
 * A type for passing around Rolodex entry JSON plus local-caching information
 */
export type CacheableRolodexEntryJson = RolodexEntryJson &
  Partial<{
    isFromCache: true
  }>

/**
 * Rolodex stores files under an entry, and so a file path in Rolodex consists
 * of an entry ID and a file path.
 */
export type RolodexFilePath = {
  entry_id: string
  path: string
}

/**
 * A Rolodex file path, plus the content type ("image/jpeg", "text/csv", etc.)
 */
export type RolodexFileRef = RolodexFilePath & {
  content_type: string
}

/**
 * A Rolodex file path, plus the signed URL used to download that file
 */
export type RolodexSignedURLResult = RolodexFilePath & {
  signed_url: string
}

/**
 * When loading entries from Rolodex any number of linked entries might be
 * returned for each entry in the main query result.
 */
export type RolodexLoadedEntryJson = {
  entry: RolodexEntryJson
  links?: Record<string, RolodexEntryJson>
}

/**
 * The different metadata endpoints rolodex supports
 */
export enum RolodexMetadataField {
  Running_On = 'running_on',
  File_Backend = 'storage_backend',
  Version = 'version',
}

export type FetcherType<T> = Partial<
  PublicConfiguration<T, Error | unknown, BareFetcher<T>>
>

export type RolodexAuditLogObject = {
  [key: string]: RolodexAuditLogJson[]
}

export type RolodexAuditLogJson = {
  id: number
  object_id: string
  old_value: RolodexEntryJson
  new_value: RolodexEntryJson
  metadata: RolodexAuditLogMetadataJson
  timestamp: string
  user_id: string
}

export type RolodexAuditLogMetadataJson = {
  user_id: string
  user_name: string | undefined
  user_email: string
  transaction_id: string
  api_operation: string
}
